import {
  DrawerOverlay,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Stack,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { Field, Form } from "@saas-ui/react";
import { useEffect, useState } from "react";
import { useError } from "../../hooks/useError";
import { useSuccess } from "../../hooks/useSuccess";
import { Api } from "../../utils/Api";
import { settlementValidationSchema } from "../../utils/form";
import {
  Settlement,
  SettlementStatus,
  SETTLEMENT_TYPE_OPTIONS,
  SOURCE_CRYPTO_EXCHANGE_OPTIONS,
  SOURCE_FI_OPTIONS,
  STATUS_OPTIONS,
  TARGET_CRYPTO_EXCHANGE_OPTIONS,
  TARGET_FI_OPTIONS,
} from "../../utils/typings";

interface props {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  data?: Settlement;
}

interface Values {
  email: string;
  password: string;
}

export const UpdateDeleteDrawer = ({
  isOpen,
  onClose,
  onOpen,
  data,
  ...rest
}: props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settlement, setSettlement] = useState<any>(data);

  const {
    onClose: onDeleteClose,
    onOpen: onDeleteOpen,
    isOpen: isDeleteOpen,
  } = useDisclosure();

  const { handleSuccess } = useSuccess();
  const { handleError } = useError();

  useEffect(() => {
    setSettlement(data);
  }, [data]);

  const onSubmit = async (values: Values) => {
    try {
      settlement["settlementAmt"] = settlement.sendAmt;
      delete settlement.sendAmt;

      await Api.updateSettlement(data?.settlementId || "", settlement);
      onClose();
      handleSuccess("Settlement successfully updated");
    } catch (error) {
      handleError(error);
    }
  };

  const deleteSettlement = async () => {
    try {
      await Api.deletSettlement(data?.settlementId);
      onDeleteClose();
      onClose();
      handleSuccess("Settlement successfully deleted");
    } catch (error) {
      handleError(error);
    }
  };

  const handleOnChange = (name: string) => (incoming: any) => {
    const value = incoming?.target?.value || incoming;
    setSettlement({
      ...(settlement || data),
      [name]: value,
    });
  };

  return (
    <>
      <Drawer onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent
          maxW={{ base: "90%", md: "50%", lg: "35%" }}
          bg="white"
          color="#54565A"
          borderColor="#54565A"
        >
          <DrawerHeader
            borderBottomWidth="1px"
            justifyContent="space-between"
            w="full"
          >
            Update Settlement
          </DrawerHeader>
          <DrawerBody>
            <Form schema={settlementValidationSchema} onSubmit={onSubmit}>
              <Stack spacing="6">
                <Stack spacing="5">
                  <Field
                    name="status"
                    placeholder="Settlement Status"
                    type="select"
                    label="Settlement Status*"
                    options={STATUS_OPTIONS}
                    value={
                      settlement?.status ===
                      SettlementStatus.SETTLEMENT_COMPLETED
                        ? "Completed"
                        : "Pending"
                    }
                    onChange={handleOnChange("status")}
                  />
                  <Field
                    name="settlementType"
                    placeholder="Settlement Type"
                    type="select"
                    label="Settlement Type*"
                    options={SETTLEMENT_TYPE_OPTIONS}
                    value={settlement?.settlementType}
                    onChange={handleOnChange("settlementType")}
                  />
                  <Field
                    name="sourceFi"
                    placeholder="Select one"
                    type="select"
                    label="Source FI"
                    options={SOURCE_FI_OPTIONS}
                    value={settlement?.sourceFi}
                    onChange={handleOnChange("sourceFi")}
                  />
                  <Field
                    name="targetFi"
                    placeholder="Select one"
                    type="select"
                    label="Target FI*"
                    options={TARGET_FI_OPTIONS}
                    value={settlement?.targetFi}
                    onChange={handleOnChange("targetFi")}
                  />
                  <Field
                    name="sourceCcy"
                    placeholder="Source Currency"
                    type="text"
                    label="Source Currency*"
                    value={settlement?.sourceCcy}
                    onChange={handleOnChange("sourceCcy")}
                  />
                  <Field
                    name="transferCryptoCcy"
                    placeholder="Transfer Cryptocurrency"
                    type="text"
                    label="Transfer Cryptocurrency*"
                    value={settlement?.transferCryptoCcy}
                    onChange={handleOnChange("transferCryptoCcy")}
                  />
                  <Field
                    name="targetFiatCcy"
                    placeholder="Target Fiat Currency"
                    type="text"
                    label="Target FIat Currency*"
                    value={settlement?.targetFiatCcy}
                    onChange={handleOnChange("targetFiatCcy")}
                  />
                  <Field
                    name="settlementAmt"
                    placeholder="Settlement Amount"
                    type="number"
                    label="Settlement Amount*"
                    value={settlement?.settlementAmt || settlement?.sendAmt}
                    onChange={handleOnChange("settlementAmt")}
                  />
                  <Field
                    name="receivedAmt"
                    placeholder="Received Amount"
                    type="number"
                    label="Received Amount*"
                    value={settlement?.receivedAmt || ""}
                    onChange={handleOnChange("receivedAmt")}
                  />
                  <Field
                    name="almondFee"
                    placeholder="Almond fee"
                    type="number"
                    label="Almond fee*"
                    value={settlement?.almondFee || ""}
                    onChange={handleOnChange("almondFee")}
                  />
                  <Field
                    name="sourceCryptoExchange"
                    placeholder="Source Crypto Exchange"
                    type="select"
                    label="Source Crypto Exchange"
                    options={SOURCE_CRYPTO_EXCHANGE_OPTIONS}
                    value={settlement?.sourceCryptoExchange}
                    onChange={handleOnChange("sourceCryptoExchange")}
                  />
                  <Field
                    name="targetCryptoExchange"
                    placeholder="Target Crypto Exchange"
                    type="select"
                    label="Target Crypto Exchange*"
                    options={TARGET_CRYPTO_EXCHANGE_OPTIONS}
                    value={settlement?.targetCryptoExchange}
                    onChange={handleOnChange("targetCryptoExchange")}
                  />
                </Stack>
                <Stack spacing="6" direction="row" w="100%">
                  <Button
                    variant="error"
                    onClick={onDeleteOpen}
                    w="45%"
                    bg="red"
                    color="white"
                  >
                    Delete
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isSubmitting}
                    bg="#00AE42"
                    w="55%"
                  >
                    Update
                  </Button>
                </Stack>
              </Stack>
            </Form>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent bg="white" color="#54565A">
          <ModalHeader>Delete settlement?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure to delete this settlement? This action is not undoable
          </ModalBody>

          <ModalFooter>
            <Button bg="#54565A" mr={3} onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button bg="red" onClick={deleteSettlement}>
              Yes, delete this settlement
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
